import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { VenueTableFilterTypes } from "~pages/Venues/enums/venueTableFilterTypes.ts";
import { useGetVenueTableColumns } from "~pages/Venues/hooks/useGetVenueTableColumns.tsx";
import { useGetVenueTableFilters } from "~pages/Venues/hooks/useGetVenueTableFilters.tsx";
import { useVenueService } from "~pages/Venues/hooks/useVenueService.ts";
import { defaultVenuesExportFieldNames, venuesExportFields } from "~pages/Venues/VenueListPage/constants/exportFields.ts";
import { useFetchVenues } from "~pages/Venues/VenueListPage/hooks/useFetchVenues.ts";

const DEFAULT_SORTER = { columnKey: "nameLocal", order: "descend" as const };

const VenueListPage = () => {
  const paginationConfig = useHandlePagination(20, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId } = useParams();
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const listUrl = `/${customerId}/admin/${sport}/${organizationId}/venues`;
  const handleAdd = () => navigate(`/${customerId}/admin/${sport}/${organizationId}/venues/manage/`, { state: { from: listUrl } });
  const [searchText, setSearchText] = useState(location.state?.search || "");
  const [massEditMode, setMassEditMode] = useState(false);

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.venues.listPage;
  const dataTableImportConfiguration = uiConfiguration.pages.venues.listPage.importConfig;
  const [sortBy, setSortBy] = useState<string | null>(null);
  const { currentFilter, filters, handleFilterChange } = useGetVenueTableFilters();

  const handleSearch = useCallback(
    (value: string) => {
      paginationConfig.resetPagination();
      setSearchText(value);
    },
    [paginationConfig],
  );

  const handleFiltersChange = useCallback(
    (updatedFilter: Record<string, unknown>) => {
      paginationConfig.resetPagination();
      handleFilterChange(updatedFilter);
    },
    [handleFilterChange, paginationConfig],
  );

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      sortBy: sortBy || "",
      status: currentFilter[VenueTableFilterTypes.Status]?.value,
      countryCode: currentFilter[VenueTableFilterTypes.CountryCode]?.value,
      nameLocalContains: searchText,
    }),
    [currentFilter, paginationConfig.offset, paginationConfig.pageSize, searchText, sortBy],
  );

  const { data, refetch, isPending } = useFetchVenues({
    customParams: listParams,
    enabled: sortBy !== null,
  });

  const { deleteVenue, isDeletePending, importVenues, exportVenues, isExportPending } = useVenueService();

  const handleDelete = useCallback(
    async (venueId: string) => {
      return await deleteVenue(venueId);
    },
    [deleteVenue],
  );

  const columns = useGetVenueTableColumns({ massEditMode });

  const onMassEditModeChangedHandler = useCallback((checked: boolean) => {
    setMassEditMode(checked);
  }, []);

  const dataSource = data?.data || [];

  const getNavigateUrl = (venueId: string) => `/${customerId}/admin/${sport}/${organizationId}/venues/manage/${venueId}`;

  return (
    <DataTable
      handleRefresh={async () => {
        await refetch();
      }}
      title={formatMessage({ id: "venuesListPage.title" })}
      columns={columns}
      rowActions={{
        base: {
          delete: {
            onClick: (row) => handleDelete(row.venueId),
            loading: isDeletePending,
          },
          manage: {
            onClick: (row) => navigate(getNavigateUrl(row.venueId), { state: { from: listUrl } }),
          },
        },
      }}
      rowNavigation={{
        getNavigateUrl: (row) => getNavigateUrl(row.venueId),
      }}
      rowKey={"venueId"}
      rowNameKey={"nameLocal"}
      dataSource={dataSource}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.venues`}
      isDataLoading={isPending}
      handleAdd={handleAdd}
      addButtonIntlKey={"venue"}
      filtersConfig={{
        currentFilter,
        filters,
        handleFilterChange: handleFiltersChange,
      }}
      searchConfig={{
        handleSearch,
        searchText,
      }}
      onMassEditModeChangedHandler={onMassEditModeChangedHandler}
      massEditMode={massEditMode}
      columnSortConfig={{
        sorterLocalStorageKey: `${sport}.tables.settings.sort.venues`,
        defaultSorter: DEFAULT_SORTER,
        onSortChange: setSortBy,
      }}
      exportConfig={{
        handleExport: exportVenues,
        loading: isExportPending,
        defaultFields: defaultVenuesExportFieldNames,
        groupedFields: venuesExportFields,
        localStorageKey: "export.venues",
      }}
      importConfig={{
        ...dataTableImportConfiguration,
        handleImport: importVenues,
      }}
      permissionNames={[PermissionNames.VENUE]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default VenueListPage;
